import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useComposedCssClasses } from '../../../hooks/useComposedCssClasses';
import { ThumbsFeedback, builtInCssClasses as thumbsFeedbackCssClasses } from '../../ThumbsFeedback';
import { renderHighlightedValue } from '../../utils/renderHighlightedValue';
import PropTypes from 'prop-types';
/**
 * Default Tailwind styles for the StandardCardDisplay.
 */
const builtInCssClasses = {
    container: 'flex flex-col justify-between border border-gray-200 rounded-lg mb-4 p-4 shadow-sm',
    header: 'flex text-neutral-dark',
    body: 'flex justify-end pt-2.5 text-base',
    cta1: 'whitespace-nowrap bg-primary text-white font-medium rounded-lg py-2 px-5 shadow',
    cta2: 'whitespace-nowrap bg-white text-primary font-medium rounded-lg py-2 px-5 mt-2 shadow',
    title: 'text-lg font-medium',
    thumbsFeedbackContainer: 'flex justify-end mt-4 text-sm text-gray-500 font-medium',
    thumbsUpIcon: thumbsFeedbackCssClasses.thumbsUpIcon,
    thumbsDownIcon: thumbsFeedbackCssClasses.thumbsDownIcon
};
/**
 * The display portion of the {@link StandardCard}.
 *
 * @param props - Contains a title, an optional description, and up to two CTAs. Click handlers for
 *                the various buttons and links are also supplied. The props can also include custom
 *                styling.
 * @returns - The rendered Result card.
 */
function StandardCardDisplay(props) {
    var _a;
    const { title, link, description, customCssClasses, clickHandlers, showFeedbackButtons, cta1, cta2 } = props;
    const cssClasses = useComposedCssClasses(builtInCssClasses, customCssClasses);
    function renderTitle(title, link) {
        const titleJsx = renderHighlightedValue(title, { highlighted: 'font-bold', nonHighlighted: 'font-medium' });
        return (link
            ? _jsx("a", Object.assign({ href: link, className: 'text-lg font-medium text-primary hover:underline focus:underline', onClick: clickHandlers.handleTitleClick }, { children: titleJsx }), void 0)
            : _jsx("div", Object.assign({ className: cssClasses.title }, { children: titleJsx }), void 0));
    }
    function renderCTAs(cta1, cta2) {
        if (cta1 || cta2) {
            return (_jsxs("div", Object.assign({ className: 'flex flex-col justify-end ml-4' }, { children: [cta1 &&
                        _jsx("button", Object.assign({ className: cssClasses.cta1, onClick: clickHandlers.handleCtaClick }, { children: cta1.label }), void 0), cta2 &&
                        _jsx("button", Object.assign({ className: cssClasses.cta2, onClick: clickHandlers.handleCtaClick }, { children: cta2.label }), void 0)] }), void 0));
        }
        return null;
    }
    function renderDescription(text) {
        if (text) {
            return (_jsx("div", Object.assign({ className: 'w-full' }, { children: renderHighlightedValue(text, { highlighted: 'font-semibold', nonHighlighted: 'font-normal' }) }), void 0));
        }
        return null;
    }
    function renderFeedbackIcons() {
        if (showFeedbackButtons) {
            return (_jsx(ThumbsFeedback, { feedbackText: '', onClick: clickHandlers.handleFeedbackClick, customCssClasses: cssClasses }, void 0));
        }
        return null;
    }
    return (_jsxs("div", Object.assign({ className: cssClasses.container }, { children: [_jsx("div", Object.assign({ className: cssClasses.header }, { children: renderTitle(title, link) }), void 0), ((_a = description !== null && description !== void 0 ? description : cta1) !== null && _a !== void 0 ? _a : cta2) &&
                _jsxs("div", Object.assign({ className: cssClasses.body }, { children: [renderDescription(description), renderCTAs(cta1, cta2)] }), void 0), renderFeedbackIcons()] }), void 0));
}
/**
 * The PropTypes for the StandardCardDisplay component. Note that props known at compile-time
 * are not included here. Type-checking of these props is taken care of by TypeScript.
 */
StandardCardDisplay.propTypes = {
    title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            matchedSubstrings: PropTypes.arrayOf(PropTypes.shape({
                length: PropTypes.number.isRequired,
                offset: PropTypes.number.isRequired
            })).isRequired
        })
    ]).isRequired,
    link: PropTypes.string,
    description: PropTypes.string,
    cta1: PropTypes.shape({
        label: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
        linkType: PropTypes.string.isRequired
    }),
    cta2: PropTypes.shape({
        label: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
        linkType: PropTypes.string.isRequired
    })
};
export { StandardCardDisplay };
//# sourceMappingURL=StandardCardDisplay.js.map